"use client";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CampaignImageUpload } from "@/components/campaigns/CampaignImageUpload";
import { CategoryMultiSelect } from "@/components/campaigns/CategoryMultiSelect";
import LocationMapPicker from "@/components/maps/LocationMapPicker";
import type { CampaignFormData, TenantColors } from "./types";

interface CampaignDetailsStepProps {
  campaignData: CampaignFormData;
  onCampaignChange: (field: keyof CampaignFormData, value: string | boolean) => void;
  onCategoriesChange: (categories: CampaignFormData["categories"]) => void;
  onLocationChange: (locationData: {
    address: string;
    latitude?: number;
    longitude?: number;
    radiusMiles?: number | null;
    hideAddress?: boolean;
  }) => void;
  tenantColors?: TenantColors | null;
}

export function CampaignDetailsStep({
  campaignData,
  onCampaignChange,
  onCategoriesChange,
  onLocationChange,
}: CampaignDetailsStepProps) {
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Campaign Details</h2>

      <div>
        <Label htmlFor="title">Campaign Title *</Label>
        <Input
          id="title"
          value={campaignData.title}
          onChange={(e) => onCampaignChange("title", e.target.value)}
          className="mt-1"
          placeholder="Enter campaign title"
        />
      </div>

      <div>
        <div className="flex items-center space-x-2">
          <input
            id="isPublic"
            type="checkbox"
            checked={campaignData.isPublic}
            onChange={(e) => onCampaignChange("isPublic", e.target.checked)}
            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
          />
          <Label htmlFor="isPublic">Show in public directory</Label>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          When enabled, this campaign will appear in the public browse directory. Disable to keep the campaign unlisted — participants will need a direct link.
        </p>
      </div>

      <div>
        <Label className="mb-2 block">Authentication Mode</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => onCampaignChange("authMode", "PLATFORM")}
            className={`flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all ${
              campaignData.authMode === "PLATFORM"
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 ${
              campaignData.authMode === "PLATFORM"
                ? "border-blue-500 bg-blue-500"
                : "border-gray-300"
            }`}>
              {campaignData.authMode === "PLATFORM" && (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-white" />
                </div>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Platform Login</p>
              <p className="text-xs text-gray-500 mt-0.5">Users create an account or log in to claim assets. Full profile and tracking.</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => onCampaignChange("authMode", "CHAIN_NATIVE")}
            className={`flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all ${
              campaignData.authMode === "CHAIN_NATIVE"
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 ${
              campaignData.authMode === "CHAIN_NATIVE"
                ? "border-blue-500 bg-blue-500"
                : "border-gray-300"
            }`}>
              {campaignData.authMode === "CHAIN_NATIVE" && (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-white" />
                </div>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Chain Native Only</p>
              <p className="text-xs text-gray-500 mt-0.5">Users connect a wallet or enter their address directly. No platform account required.</p>
            </div>
          </button>
        </div>
      </div>

      <div>
        <Label htmlFor="headline">Headline *</Label>
        <Input
          id="headline"
          value={campaignData.headline}
          onChange={(e) => {
            if (e.target.value.length <= 255) {
              onCampaignChange("headline", e.target.value);
            }
          }}
          className="mt-1"
          placeholder="A short tagline for browse tiles"
          maxLength={255}
        />
        <p className="mt-1 text-xs text-gray-500">
          {campaignData.headline.length}/255 characters
        </p>
      </div>

      <div>
        <Label htmlFor="description">Description *</Label>
        <textarea
          id="description"
          rows={3}
          value={campaignData.description}
          onChange={(e) => onCampaignChange("description", e.target.value)}
          className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 px-3 py-2 text-sm shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          placeholder="Describe your scavenger hunt"
        />
      </div>

      <CampaignImageUpload
        id="coverImageUrl"
        label="Featured Image"
        value={campaignData.coverImageUrl}
        onChange={(url) => onCampaignChange("coverImageUrl", url)}
        description="This image will be displayed on the campaign page and in campaign listings."
      />

      <div>
        <Label htmlFor="categories">Categories (Select up to 3)</Label>
        <CategoryMultiSelect
          selected={campaignData.categories}
          onChange={onCategoriesChange}
          max={3}
        />
      </div>

      <div>
        <Label>Campaign Location</Label>
        <LocationMapPicker
          initialLocation={{
            address: campaignData.streetAddress,
            latitude: campaignData.latitude,
            longitude: campaignData.longitude,
          }}
          onLocationChange={(locationData) => onLocationChange(locationData)}
          className="mt-2"
        />
      </div>

      <div>
        <div className="flex items-center space-x-2">
          <input
            id="showMap"
            type="checkbox"
            checked={campaignData.showMap}
            onChange={(e) => onCampaignChange("showMap", e.target.checked)}
            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
          />
          <Label htmlFor="showMap">Show map to participants</Label>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          When enabled, participants will see a map showing all hunt locations with their configured radius areas.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="startDate">Start Date &amp; Time</Label>
          <div className="mt-1 flex rounded-md border border-input shadow-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 overflow-hidden">
            <input
              id="startDate"
              type="date"
              value={campaignData.startAt ? campaignData.startAt.split('T')[0] : ''}
              onChange={(e) => {
                const date = e.target.value;
                const time = campaignData.startAt?.split('T')[1] || '00:00';
                onCampaignChange("startAt", date ? `${date}T${time}` : '');
              }}
              className="flex-1 min-w-0 bg-white text-gray-900 px-3 py-2 text-sm border-0 focus:outline-none focus:ring-0"
            />
            <div className="w-px bg-gray-200" />
            <input
              id="startTime"
              type="time"
              value={campaignData.startAt?.split('T')[1] || ''}
              onChange={(e) => {
                const date = campaignData.startAt?.split('T')[0] || '';
                const time = e.target.value;
                if (date) {
                  onCampaignChange("startAt", `${date}T${time || '00:00'}`);
                }
              }}
              className="w-[165px] flex-shrink-0 bg-white text-gray-900 px-3 py-2 text-sm border-0 focus:outline-none focus:ring-0"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="endDate">End Date &amp; Time</Label>
          <div className="mt-1 flex rounded-md border border-input shadow-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 overflow-hidden">
            <input
              id="endDate"
              type="date"
              value={campaignData.endAt ? campaignData.endAt.split('T')[0] : ''}
              onChange={(e) => {
                const date = e.target.value;
                const time = campaignData.endAt?.split('T')[1] || '23:59';
                onCampaignChange("endAt", date ? `${date}T${time}` : '');
              }}
              className="flex-1 min-w-0 bg-white text-gray-900 px-3 py-2 text-sm border-0 focus:outline-none focus:ring-0"
            />
            <div className="w-px bg-gray-200" />
            <input
              id="endTime"
              type="time"
              value={campaignData.endAt?.split('T')[1] || ''}
              onChange={(e) => {
                const date = campaignData.endAt?.split('T')[0] || '';
                const time = e.target.value;
                if (date) {
                  onCampaignChange("endAt", `${date}T${time || '23:59'}`);
                }
              }}
              className="w-[165px] flex-shrink-0 bg-white text-gray-900 px-3 py-2 text-sm border-0 focus:outline-none focus:ring-0"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
